import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { listRejectionReasons, type RejectionReasonOption } from '../../api/rejectionReasons.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { SubmissionPhotos, type SubmissionPhoto } from './ReviewPanels.js';

/**
 * M10 L10.04 — Photo Validation (§10).
 *
 * Lists the site photos the painter submitted for the review's job
 * (`GET /v1/quality/reviews/:reviewId/photos`), each with the decision recorded
 * on it. A photo is looked at on this same screen, in the viewer on the right,
 * through M09's signed photo link, and the photo being looked at is the one the
 * decision is recorded against, via the 9-point brand/quality checklist
 * (BR-M10-10..14) and `POST /v1/quality/reviews/:reviewId/photos/:photoId`.
 * Rejections must carry a reason drawn from the M05 Rejection Reason Master (§15).
 */
type PhotoDecision = 'APPROVED' | 'REJECTED' | 'ADDITIONAL_REQUESTED';


const CHECKLIST_ITEMS = [
  ['chkBrandLogoVisible', 'Brand logo visible'],
  ['chkBrandColorsCorrect', 'Brand colors correct'],
  ['chkBrandGuidelinesOk', 'Brand guidelines followed'],
  ['chkAreaFullyCovered', 'Area fully covered'],
  ['chkNoIncomplete', 'No incomplete sections'],
  ['chkPhotoQualityOk', 'Photo quality acceptable'],
  ['chkPhotoCountOk', 'Photo count sufficient'],
  ['chkGpsMetadataPresent', 'GPS metadata available'],
  ['chkTimestampPresent', 'Timestamp available'],
] as const;

type ChecklistKey = (typeof CHECKLIST_ITEMS)[number][0];

export function QualityPhotoValidationPage(): ReactNode {
  const { reviewId = '' } = useParams<{ reviewId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const photoLabel = (p: SubmissionPhoto): string => [p.photoType, at(p.capturedAt)].join(' · ');
  const photosPath = `/v1/quality/reviews/${reviewId}/photos`;
  const [rows, setRows] = useState<readonly SubmissionPhoto[]>([]);
  const [reasons, setReasons] = useState<readonly RejectionReasonOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Apart from `error`, which the photo list read clears on success.
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [photoId, setPhotoId] = useState('');
  const [decision, setDecision] = useState<PhotoDecision>('APPROVED');
  const [rejectReasonCode, setRejectReasonCode] = useState('');
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [checklist, setChecklist] = useState<Record<ChecklistKey, boolean>>(() =>
    Object.fromEntries(CHECKLIST_ITEMS.map(([k]) => [k, false])) as Record<ChecklistKey, boolean>,
  );

  const load = useCallback(async () => {
    try {
      const data = await apiRequest<readonly SubmissionPhoto[]>(photosPath, { token });
      setRows(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [photosPath, token]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    let alive = true;
    listRejectionReasons(token, 'M10').then((r) => { if (alive) setReasons(r); }).catch((e) => { if (alive) setLookupError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  const onSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setActionError(null);
    try {
      await apiRequest(`/v1/quality/reviews/${reviewId}/photos/${photoId.trim()}`, {
        method: 'POST', token,
        body: {
          checklist,
          decision,
          rejectReasonCode: decision === 'REJECTED' ? rejectReasonCode.trim() : undefined,
          rejectRemarks: decision === 'REJECTED' ? (rejectRemarks.trim() || undefined) : undefined,
        },
      });
      setPhotoId(''); setRejectReasonCode(''); setRejectRemarks('');
      setChecklist(Object.fromEntries(CHECKLIST_ITEMS.map(([k]) => [k, false])) as Record<ChecklistKey, boolean>);
      await load();
    } catch (err) { setActionError((err as Error).message); }
    finally { setBusy(false); }
  }, [reviewId, token, photoId, checklist, decision, rejectReasonCode, rejectRemarks, load]);

  // The photo being looked at is the one the decision is recorded against.
  const onView = useCallback((p: SubmissionPhoto) => setPhotoId(p.photoId), []);

  const rejectInvalid = decision === 'REJECTED' && !rejectReasonCode.trim();

  return (
    <main aria-label="quality-photo-validation">
      <p style={{ marginBottom: 8 }}><Link to={`/quality/review/${reviewId}`}>{t('quality.common.back')}</Link></p>
      <h1 style={{ fontSize: 20, marginBottom: 12 }}>{t('quality.photo.title')}</h1>
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {lookupError && <p role="alert" style={{ color: 'var(--color-danger)' }}>{lookupError}</p>}

      <SubmissionPhotos rows={rows} onView={onView}>
      <form onSubmit={onSubmit} aria-label="photo-decision-form" style={{ maxWidth: 520, marginBottom: 24 }}>
        <RecordPicker<SubmissionPhoto>
          endpoint={photosPath}
          idField="photoId"
          label={t('quality.photo.photoId')}
          testId="pv-photo-id"
          value={photoId}
          optionLabel={photoLabel}
          onPick={setPhotoId}
        />

        <fieldset aria-label="photo-checklist" style={{ border: '1px solid var(--color-border-default)', borderRadius: 6, padding: 12, marginBottom: 12 }}>
          <legend style={{ fontSize: 13, fontWeight: 600 }}>{t('quality.photo.checklistLegend')}</legend>
          {CHECKLIST_ITEMS.map(([key]) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 4 }}>
              <input type="checkbox" data-testid={`chk-${key}`} checked={checklist[key]}
                     onChange={(e) => setChecklist((c) => ({ ...c, [key]: e.target.checked }))} />
              {t(`quality.photo.chk.${key}`)}
            </label>
          ))}
        </fieldset>

        <label htmlFor="pv-decision" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>{t('quality.common.decision')}</label>
        <select id="pv-decision" data-testid="pv-decision" value={decision}
                onChange={(e) => setDecision(e.target.value as PhotoDecision)}
                style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)', marginBottom: 12 }}>
          <option value="APPROVED">{t('quality.common.approve')}</option>
          <option value="REJECTED">{t('quality.common.reject')}</option>
          <option value="ADDITIONAL_REQUESTED">{t('quality.photo.opt.additional')}</option>
        </select>

        {decision === 'REJECTED' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            <label htmlFor="pv-reason" style={{ fontSize: 12 }}>{t('quality.photo.reason')}</label>
            <select id="pv-reason" data-testid="pv-reason" value={rejectReasonCode}
                    onChange={(e) => setRejectReasonCode(e.target.value)}
                    style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
              <option value="">{t('quality.photo.selectReason')}</option>
              {reasons.map((r) => <option key={r.reasonId} value={r.reasonCode}>{r.reasonDescription}</option>)}
            </select>
            <label htmlFor="pv-remarks" style={{ fontSize: 12 }}>{t('quality.photo.remarks')}</label>
            <textarea id="pv-remarks" data-testid="pv-remarks" value={rejectRemarks} rows={2}
                      onChange={(e) => setRejectRemarks(e.target.value)}
                      style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
          </div>
        )}

        <Button type="submit" data-testid="pv-submit" disabled={busy || !photoId.trim() || rejectInvalid} variant="primary">
          {busy ? t('quality.common.saving') : t('quality.common.save')}
        </Button>
        {actionError && <p role="alert" style={{ color: 'var(--color-danger)', marginTop: 8 }}>{actionError}</p>}
      </form>

      </SubmissionPhotos>
    </main>
  );
}
